/**
 * 家长端 · 奖品目录管理（见契约 1.3「奖品目录」）。
 *
 * ⚠️ **一套目录，不再按孩子分**：奖品是**家长作用域**的 —— 家长建一次，
 * 名下所有孩子都能看到、都能兑换（见 packages/db/src/schema.ts 的 reward 注释）。
 * 页面的默认可见范围是「所有孩子共用」，也可以显式改成「仅某个孩子可见」。
 *
 * 因此"选孩子"这个入口从**必选**变成了**筛选**：
 *   - 不选 = 看整套目录（共用 + 全部专属奖品）；
 *   - 选一个孩子 = 用他的眼睛看（只出现他能看到的那些），并显示他"还差多少分"。
 *
 * 库存语义：共用的奖品是**一个共用池** —— 任何一个孩子兑换都会让它减 1，
 * 其他孩子立刻看到同一个数变小。界面上说清楚这一点，家长才不会以为是"每人一个"。
 *
 * 系统**不预置任何奖品**（方案 3.4.3 已确认），初始为空、由家长维护：
 * 名称、所需积分、库存（留空 = 不限）、是否需要家长审批、上架/下架、可见范围。
 *
 * 另外提供"从模板一键添加"，模板只存在于界面（不写入数据库初始数据），
 * 并标注档位 —— 高频小额那一档必须至少有一个，否则积分制会在第二周失效。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { SharePermission } from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState, StatTile } from '../../components/ui/Bits';
import { useMyRewardCatalog, usePointsSummary, useRewardMutations } from '../../lib/domainQueries';
import { useParentActor } from '../../lib/ActorContext';
import { canManageReward } from '../../lib/uiMode';
import { toErrorMessage } from '../../lib/api/error';
import { earnedTotalText, spentTotalText } from '../../lib/pointsDisplay';
import { cn } from '../../lib/utils';
import type { Reward, RewardStudent } from '../../lib/api/types';

/** 奖品模板：只作为界面里的可选项（见方案 3.4.3）。 */
const REWARD_TEMPLATES = [
  { name: '30 分钟游戏时间', costPoints: 50, tier: '高频小额' },
  { name: '选一次晚餐菜单', costPoints: 80, tier: '高频小额' },
  { name: '一本想买的书', costPoints: 200, tier: '中频中额' },
  { name: '周末去游乐场', costPoints: 300, tier: '低频大额' },
  { name: '一次"免作业券"', costPoints: 500, tier: '慎用' },
] as const;

interface RewardFormState {
  name: string;
  description: string;
  costPoints: string;
  unlimitedStock: boolean;
  stock: string;
  needApproval: boolean;
  imageUrl: string;
  active: boolean;
  /** 可见范围：null = 所有孩子共用；否则只给这个孩子。 */
  scopeStudentId: string | null;
}

const EMPTY_FORM: RewardFormState = {
  name: '',
  description: '',
  costPoints: '50',
  unlimitedStock: true,
  stock: '1',
  needApproval: true,
  imageUrl: '',
  active: true,
  scopeStudentId: null,
};

function formFromReward(reward: Reward): RewardFormState {
  return {
    name: reward.name,
    description: reward.description,
    costPoints: String(reward.costPoints),
    unlimitedStock: reward.stock === null,
    stock: reward.stock === null ? '1' : String(reward.stock),
    needApproval: reward.needApproval,
    imageUrl: reward.imageUrl ?? '',
    active: reward.active,
    scopeStudentId: reward.studentId ?? null,
  };
}

/** 共用还是专属（后端给了 `scope` 就信它，否则按 `studentId` 推断）。 */
function isShared(reward: Reward): boolean {
  return reward.scope ? reward.scope === 'shared' : reward.studentId == null;
}

function scopeLabel(reward: Reward, students: RewardStudent[]): string {
  if (isShared(reward)) return '所有孩子共用';
  const nickname =
    reward.studentNickname ?? students.find((s) => s.id === reward.studentId)?.nickname;
  return nickname ? `仅 ${nickname} 可见` : '仅指定孩子可见';
}

export function ParentRewardsPage() {
  const { id: routeStudentId } = useParams();
  const [params] = useSearchParams();
  const toast = useToast();

  // 一套目录（家长作用域）：不再"先选一个孩子"才能建奖品
  const catalogQuery = useMyRewardCatalog();
  const students = catalogQuery.data?.students ?? [];

  /**
   * 我名下的权限档位（取最高的一档）。
   * 与后端门禁同源：`canManageRewards` 只要求"在**某个**孩子上是 owner/editor"，
   * 所以这里只要有任意一个孩子可管理，就显示维护按钮；真正的门禁在服务端。
   */
  const bestPermission: SharePermission = useMemo(() => {
    if (students.some((s) => s.permission === 'owner')) return 'owner';
    if (students.some((s) => s.permission === 'editor')) return 'editor';
    return 'viewer';
  }, [students]);
  const actor = useParentActor(bestPermission);
  const canManage = canManageReward(actor);

  // "选孩子"= 筛选/预览：URL 上带了孩子就从它开始（孩子详情页的入口）
  const urlStudentId = routeStudentId ?? params.get('studentId') ?? '';
  const [filterId, setFilterId] = useState(urlStudentId);
  useEffect(() => setFilterId(urlStudentId), [urlStudentId]);

  const pointsQuery = usePointsSummary(filterId || null);
  const mutations = useRewardMutations(filterId || null);

  const [form, setForm] = useState<RewardFormState | null>(null);
  const [editing, setEditing] = useState<Reward | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Reward | null>(null);

  const allRewards = catalogQuery.data?.rewards ?? [];
  const notReady = catalogQuery.data?.notReady ?? false;
  /** 选了孩子 = 用他的眼睛看这套目录（专属给别人的、下架的都不会出现）。 */
  const rewards = filterId
    ? allRewards.filter((reward) => isShared(reward) || reward.studentId === filterId)
    : allRewards;

  const filterStudent = students.find((s) => s.id === filterId) ?? null;

  /**
   * 档位自动推断：让家长一眼看出"是不是全是大件"。
   * 阈值参考方案 3.4.3 的模板（50 / 200 / 300）。
   */
  const tiers = useMemo(() => {
    const active = rewards.filter((reward) => reward.active);
    return {
      high: active.filter((reward) => reward.costPoints <= 100).length,
      mid: active.filter((reward) => reward.costPoints > 100 && reward.costPoints <= 250).length,
      low: active.filter((reward) => reward.costPoints > 250).length,
      active: active.length,
    };
  }, [rewards]);

  const openCreate = (preset?: { name: string; costPoints: number }) => {
    setEditing(null);
    setFormError(null);
    // 默认「所有孩子共用」——这就是这次的默认语义
    setForm(
      preset
        ? { ...EMPTY_FORM, name: preset.name, costPoints: String(preset.costPoints) }
        : { ...EMPTY_FORM },
    );
  };

  const openEdit = (reward: Reward) => {
    setEditing(reward);
    setFormError(null);
    setForm(formFromReward(reward));
  };

  const submit = async () => {
    if (!form) return;
    const name = form.name.trim();
    const costPoints = Number(form.costPoints);
    if (!name) {
      setFormError('请填写奖品名称');
      return;
    }
    if (!Number.isFinite(costPoints) || costPoints < 1) {
      setFormError('所需积分至少为 1');
      return;
    }
    const stock = form.unlimitedStock ? null : Number(form.stock);
    if (!form.unlimitedStock && (!Number.isFinite(stock) || (stock ?? 0) < 0)) {
      setFormError('库存要填 0 或正整数；想不限量就把「不限量」勾上');
      return;
    }

    const payload = {
      name,
      description: form.description.trim(),
      costPoints: Math.round(costPoints),
      stock,
      needApproval: form.needApproval,
      imageUrl: form.imageUrl.trim() || null,
      active: form.active,
      // null = 所有孩子共用；给了 id = 仅那个孩子可见
      studentId: form.scopeStudentId,
    };

    setFormError(null);
    try {
      if (editing) {
        await mutations.update.mutateAsync({ id: editing.id, payload });
      } else {
        await mutations.create.mutateAsync(payload);
      }
      toast.showToast(editing ? '已保存' : '已添加奖品', 'success');
      setForm(null);
      setEditing(null);
    } catch (err) {
      setFormError(toErrorMessage(err));
    }
  };

  const toggleActive = async (reward: Reward) => {
    try {
      await mutations.update.mutateAsync({ id: reward.id, payload: { active: !reward.active } });
      toast.showToast(reward.active ? '已下架' : '已上架', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await mutations.remove.mutateAsync(pendingDelete.id);
      toast.showToast('已删除', 'success');
      setPendingDelete(null);
    } catch (err) {
      setPendingDelete(null);
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  return (
    // 从孩子的入口进来才有"返回孩子页"可回；全局 /rewards 页没有可返回的孩子。
    // 返回目标由 `lib/backNav.ts` 按路由推导（`/students/:id/rewards` → `/students/:id`），
    // 页面不再自己传 `backTo` —— 见该文件顶部"一个页面最多一个返回入口"的约定。
    <AppShell
      kind="parent"
      title={filterStudent ? `${filterStudent.nickname} · 奖品目录` : '奖品目录（全家共用）'}
      headerAction={
        canManage ? (
          <Button size="sm" onClick={() => openCreate()}>
            新增奖品
          </Button>
        ) : (
          <Badge tone="neutral">仅查看</Badge>
        )
      }
    >
      <div className="space-y-6">
        {/* ---------- 引导文案（契约 1.2 要求） ---------- */}
        <Card className="border-brand-200 bg-brand-50/60">
          <CardHeader
            title="奖品要分三档，「高频小额」必须有"
            description="只有能在一周内兑换到的小奖品，才能维持孩子的即时动力。"
          />
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl bg-white p-3">
              <p className="font-semibold text-emerald-700">高频小额</p>
              <p className="mt-1 text-xs text-slate-500">50 分上下，一周内能兑到</p>
              <p className="mt-1 text-lg font-bold text-slate-800">{tiers.high} 个</p>
            </div>
            <div className="rounded-xl bg-white p-3">
              <p className="font-semibold text-brand-700">中频中额</p>
              <p className="mt-1 text-xs text-slate-500">200 分上下，两三周一次</p>
              <p className="mt-1 text-lg font-bold text-slate-800">{tiers.mid} 个</p>
            </div>
            <div className="rounded-xl bg-white p-3">
              <p className="font-semibold text-amber-700">低频大额</p>
              <p className="mt-1 text-xs text-slate-500">300 分以上，给长期盼头</p>
              <p className="mt-1 text-lg font-bold text-slate-800">{tiers.low} 个</p>
            </div>
          </div>
          {tiers.active > 0 && tiers.high === 0 && (
            <p className="mt-3 rounded-xl bg-amber-100 px-3 py-2 text-sm text-amber-900">
              ⚠️ 现在一个「高频小额」奖品都没有。孩子攒不到东西，积分制通常会在第二周失效 ——
              建议先加一个 50 分左右的小奖励。
            </p>
          )}
          <p className="mt-3 text-xs text-slate-500">
            系统不预置任何奖品，每个家庭的激励标准可以完全不同。库存留空表示不限量。
            默认「所有孩子共用」：建一次，家里每个孩子都能看到、都能兑换，
            但扣的始终是各自的分（积分不共享），兑换记录也分得清是谁换的。
          </p>
        </Card>

        {/* ---------- 余额（每个孩子各一行，一眼看出谁换得起） ---------- */}
        {students.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {students.map((student) => (
              <StatTile
                key={student.id}
                label={`${student.nickname} 余额`}
                value={student.balance}
                tone="amber"
              />
            ))}
            <StatTile label="在架奖品" value={tiers.active} />
            {filterId && (
              <StatTile
                label="累计获得 / 消费"
                value={
                  pointsQuery.data?.notReady
                    ? '—'
                    : // 同一个显示口径：累计消费真 0 时写「0」，不写「-0」
                      // （见 lib/pointsDisplay.ts；"冲正"不算消费）
                      `${earnedTotalText(pointsQuery.data?.totalEarned)} / ${spentTotalText(pointsQuery.data?.totalSpent)}`
                }
                tone="slate"
                hint={pointsQuery.data?.notReady ? '积分接口尚未就绪' : undefined}
              />
            )}
          </div>
        )}

        {/* ---------- 筛选（原来的"选孩子"入口，现在只是筛选） ---------- */}
        {students.length > 1 && (
          <Card>
            <Select
              label="按孩子筛选（可选）"
              value={filterId}
              onChange={(e) => setFilterId(e.target.value)}
              hint="目录是全家共用的；选一个孩子，可以看到他能兑换哪些奖品、还差多少分。"
            >
              <option value="">所有孩子（看整套目录）</option>
              {students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.nickname}
                </option>
              ))}
            </Select>
          </Card>
        )}

        {/* ---------- 模板一键添加 ---------- */}
        {canManage && (
          <Card>
            <CardHeader
              title="从模板一键添加"
              description="这些只是界面里的可选项，不会自动写进你的奖品目录。"
            />
            <div className="flex flex-wrap gap-2">
              {REWARD_TEMPLATES.map((template) => (
                <button
                  key={template.name}
                  type="button"
                  onClick={() => openCreate({ name: template.name, costPoints: template.costPoints })}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  <span className="block font-medium text-slate-800">{template.name}</span>
                  <span className="text-xs text-slate-500">
                    {template.costPoints} 分 · {template.tier}
                  </span>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* ---------- 目录 ---------- */}
        {catalogQuery.isLoading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : catalogQuery.isError ? (
          <ErrorState
            message={toErrorMessage(catalogQuery.error)}
            onRetry={() => void catalogQuery.refetch()}
          />
        ) : notReady ? (
          <NotReadyState feature="奖品目录" />
        ) : students.length === 0 ? (
          <EmptyState
            icon="🧒"
            title="先添加一个孩子"
            description="奖品是挂在家长名下的：先有孩子，才有谁能兑换。"
            action={
              <Link to="/" className="mt-2">
                <Button>回到孩子列表</Button>
              </Link>
            }
          />
        ) : rewards.length === 0 ? (
          <EmptyState
            icon="🎁"
            title={filterId ? '这个孩子现在还没有可兑换的奖品' : '还没有奖品 —— 点这里添加第一个'}
            description="先加一个 50 分左右的小奖励（比如「30 分钟游戏时间」），孩子一周内能兑到，动力才起得来。"
            action={
              canManage ? (
                <Button
                  className="mt-2"
                  onClick={() => openCreate({ name: '30 分钟游戏时间', costPoints: 50 })}
                >
                  添加第一个奖品
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {rewards.map((reward) => {
              const tierLabel =
                reward.costPoints <= 100 ? '高频小额' : reward.costPoints <= 250 ? '中频中额' : '低频大额';
              const shared = isShared(reward);
              // 只对"看得到这个奖品的孩子"算够不够 —— 专属给别人的孩子不该出现在这里
              const eligible = filterId
                ? students.filter(
                    (s) => s.id === filterId && (shared || reward.studentId === s.id),
                  )
                : students.filter((s) => shared || reward.studentId === s.id);
              return (
                <li key={reward.id} className="card space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-slate-900">{reward.name}</h3>
                      <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">
                        {reward.description || '没有填写说明'}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-xl bg-amber-50 px-3 py-1.5 text-sm font-bold text-amber-700">
                      {reward.costPoints} 分
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={shared ? 'brand' : 'neutral'}>{scopeLabel(reward, students)}</Badge>
                    <Badge tone={reward.active ? 'success' : 'neutral'}>
                      {reward.active ? '上架中' : '已下架'}
                    </Badge>
                    <Badge tone={reward.stock === null ? 'brand' : reward.stock > 0 ? 'neutral' : 'danger'}>
                      {reward.stock === null ? '不限量' : reward.stock > 0 ? `剩余 ${reward.stock}` : '已兑完'}
                    </Badge>
                    {reward.needApproval && <Badge tone="warn">需家长批准</Badge>}
                    <Badge tone="neutral">{tierLabel}</Badge>
                  </div>

                  {/* 每个孩子够不够换 —— 共用奖品的库存是一个池子，分数各算各的 */}
                  {eligible.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {eligible.map((student) => (
                        <span
                          key={student.id}
                          className={cn(
                            'rounded-lg px-2 py-0.5 text-xs',
                            student.balance >= reward.costPoints
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-500',
                          )}
                        >
                          {student.nickname}：
                          {student.balance >= reward.costPoints
                            ? '现在就能换'
                            : `还差 ${reward.costPoints - student.balance} 分`}
                        </span>
                      ))}
                    </div>
                  )}

                  {shared && reward.stock !== null && (
                    <p className="text-xs text-slate-500">
                      库存是全家人共用的：任何一个孩子兑换都会让它减 1（现在还剩 {reward.stock} 个）。
                    </p>
                  )}

                  {canManage && (
                    <div className="flex flex-wrap gap-1 border-t border-slate-100 pt-2">
                      <Button size="sm" variant="secondary" onClick={() => openEdit(reward)}>
                        编辑
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => void toggleActive(reward)}>
                        {reward.active ? '下架' : '上架'}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setPendingDelete(reward)}>
                        删除
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <Card>
          <CardHeader title="关于兑换与积分" />
          <ul className="list-inside list-disc space-y-1 text-sm text-slate-500">
            <li>奖品是全家共用的：建一次，所有孩子都能看到；但积分是各自的，谁换就扣谁的分。</li>
            <li>需要审批的奖品，孩子点兑换后先进入「待处理」，你同意时才扣分。</li>
            <li>不需要审批的奖品会立即扣分，之后由你标记「已发放」。</li>
            <li>积分流水只追加、不可修改；需要纠错时用「冲正」生成一笔反向记录。</li>
          </ul>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            <Link
              to={`/students/${filterId || students[0]?.id || ''}/redemptions`}
              className="font-medium text-brand-600 hover:underline"
            >
              去处理兑换审批 →
            </Link>
            <Link
              to={`/students/${filterId || students[0]?.id || ''}/points`}
              className="font-medium text-brand-600 hover:underline"
            >
              查看积分流水 →
            </Link>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            兑换记录按孩子分开（谁换的、扣了多少分一目了然），审批页也按孩子进。
          </p>
        </Card>
      </div>

      {/* ---------- 表单 ---------- */}
      <Modal
        open={form !== null}
        onClose={() => {
          setForm(null);
          setEditing(null);
        }}
        title={editing ? '编辑奖品' : '新增奖品'}
        size="md"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setForm(null);
                setEditing(null);
              }}
            >
              取消
            </Button>
            <Button loading={mutations.create.isPending || mutations.update.isPending} onClick={() => void submit()}>
              {editing ? '保存' : '添加'}
            </Button>
          </div>
        }
      >
        {form && (
          <div className="space-y-4">
            <Input
              label="奖品名称"
              value={form.name}
              maxLength={64}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="例如：30 分钟游戏时间"
            />
            <Input
              label="说明（可选）"
              value={form.description}
              maxLength={500}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Input
              label="所需积分"
              type="number"
              min={1}
              value={form.costPoints}
              onChange={(e) => setForm({ ...form, costPoints: e.target.value })}
              hint="参考：高频小额 50 上下，中频中额 200 上下，低频大额 300 以上"
            />

            <Select
              label="谁能看到"
              value={form.scopeStudentId ?? ''}
              onChange={(e) => setForm({ ...form, scopeStudentId: e.target.value || null })}
              hint="默认所有孩子共用（推荐）：建一次，家里每个孩子都能兑换，但各扣各的分。"
            >
              <option value="">所有孩子共用</option>
              {students.map((student) => (
                <option key={student.id} value={student.id}>
                  仅 {student.nickname} 可见
                </option>
              ))}
            </Select>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.unlimitedStock}
                  onChange={(e) => setForm({ ...form, unlimitedStock: e.target.checked })}
                />
                不限量（留空库存）
              </label>
              {!form.unlimitedStock && (
                <Input
                  label="库存数量"
                  type="number"
                  min={0}
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  hint={
                    form.scopeStudentId === null
                      ? '共用奖品的库存是一个池子：任何孩子兑换都会减 1'
                      : '兑完后孩子会看到「已兑完」，不会再显示兑换按钮'
                  }
                />
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.needApproval}
                onChange={(e) => setForm({ ...form, needApproval: e.target.checked })}
              />
              需要家长审批后才扣分（低龄建议开启，避免冲动兑换）
            </label>

            <Input
              label="图片链接（可选）"
              value={form.imageUrl}
              onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
              placeholder="https://..."
            />

            <Select
              label="状态"
              value={form.active ? 'active' : 'inactive'}
              onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })}
            >
              <option value="active">上架（孩子在奖品屋能看到）</option>
              <option value="inactive">下架（保留记录但不可兑换）</option>
            </Select>

            {formError && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {formError}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ---------- 删除确认 ---------- */}
      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="删除奖品"
        size="sm"
        footer={
          <div className="flex w-full gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setPendingDelete(null)}>
              取消
            </Button>
            <Button variant="danger" className="flex-1" loading={mutations.remove.isPending} onClick={() => void doDelete()}>
              确认删除
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          要删除「{pendingDelete?.name}」吗？
        </p>
        <p className="mt-2 text-xs text-slate-500">
          如果这个奖品已经被兑换过，服务端可能会拒绝删除 —— 那种情况下请改用「下架」，
          历史兑换记录需要保留（孩子的账目要能解释清楚）。
        </p>
      </Modal>
    </AppShell>
  );
}
